import lodash from 'lodash'
import keyMirror from 'keymirror'
import vtooltip, { tooltipShowEvent, tooltipHideEvent } from '@/controls/tooltip/v-tooltip' // eslint-disable-line
import * as tooltipOptions from '@/controls/tooltip/v-tooltip' // eslint-disable-line
import { decimalWithComma, integerWithComma, nonEmptyString } from '@/utils/regex'
import i18n from '@/utils/i18n'
import { dispatchNativeEvent } from '@/utils/nativeEvents'

const utils = {
  findDirectiveDataName (vnode, name) {
    return (vnode.data.directives.find(function (o) {
      return o.name === name
    }) || {}).expression
  },
  setVModelValueIfExist (value, vnode) {
    const expression = this.findDirectiveDataName(vnode, 'model')
    if (expression) {
      vnode.context[expression] = value
    }
  },
}

const defaultValues = {
  type: {
    options: keyMirror({
      integer: null,
      decimal: null,
      string: null,
    }),
    default: 'string',
  },
  pattern: {
    decimal: decimalWithComma,
    integer: integerWithComma,
    string: nonEmptyString,
  },
  showTip: {
    default: true,
  },
  tip: {
    default: i18n.t('hint.inputTip.default'),
  },
  showErrorClass: {
    default: true,
  },
  errorClass: {
    default: 'fusion-input-check-is-invalid-input',
  },
  triggerEvent: {
    default: 'blur validateFusionInputCheck',
  },
  resetEvent: {
    default: 'resetFusionInputCheck',
  },
  refreshEvent: {
    default: 'refreshFusionInputCheckTip',
  },
  postSuccessEvent: {
    default: 'fusionInputCheckSuccess',
  },
  postFailEvent: {
    default: 'fusionInputCheckFail',
  },
  required: {
    default: true,
  },
  allowEmpty: {
    options: keyMirror({
      all: null,
      partial: null,
      none: null,
    }),
  },
}

function updateElValue (el, binding, vnode) {
  dispatchNativeEvent(el, 'input')
  utils.setVModelValueIfExist(el.value, vnode)
  if (typeof deep(vnode, 'data.on.change.fns') === 'function') vnode.data.on.change.fns($.Event('change', { target: { value: el.value } }))
  if (typeof deep(vnode, 'data.on.input.fns') === 'function') vnode.data.on.input.fns($.Event('change', { target: { value: el.value } }))
}

/**
 * Create event handlers based on fusion input check directive value
 * @param directiveValue directive value
 */
function createEventHandlers ({el, binding, vnode}, directiveValue) {
  const eventHandlers = {}
  const checkValueFormat = (el, directiveValue, allowEmpty = defaultValues.allowEmpty.options.all) => {
    const pattern = directiveValue.pattern || defaultValues.pattern[directiveValue.type || defaultValues.type.default]
    const patternOnly = directiveValue.patternOnly

    let validValue = false
    if (typeof patternOnly === 'function') {
      validValue = patternOnly(el.value)
    } else {
      const emptyValueAccepted =
        ((allowEmpty === defaultValues.allowEmpty.options.all) ||
        (allowEmpty === defaultValues.allowEmpty.options.partial && ('nullable' in binding.value) && binding.value.nullable)) &&
        !(String(el.value) && (el.value || '').trim())
      switch (typeof pattern) {
        case 'object':
          validValue = emptyValueAccepted ||
            pattern.test(el.value || '')
          break
        case 'function':
          validValue = emptyValueAccepted ||
            (
              pattern(el.value) &&
              defaultValues.pattern[directiveValue.type || defaultValues.type.default].test(el.value || '')
            )
          break
        default:
          break
      }
    }
    if (validValue) {
      if (directiveValue.type &&
        ['integer', 'decimal'].includes(directiveValue.type) &&
          el.value && el.value.includes(',')) {
        el.value = el.value.replace(/,/g, '')
        updateElValue(el, binding, vnode)
      }
    }
    return validValue
  }
  /**
   * Post check success actions.
   * 1. hide tool tip if any
   * 2. remove error class if any
   * 3. call post success event if any
   */
  const postSuccessHandler = (el, directiveValue) => {
    $(el).attr('fusioninputchecksuccess', '')
    $(el).removeAttr('fusioninputcheckbg')
    $(el).removeAttr('fusioninputchecktip')
    if ('showTip' in directiveValue ? !!directiveValue.showTip : defaultValues.showTip.default) {
      $(el).trigger(tooltipHideEvent)
    }
    const errorClass = ('errorClass' in directiveValue && directiveValue.errorClass) || defaultValues.errorClass.default
    if (errorClass) {
      $(el).removeClass(errorClass)
    }
    if ('postSuccessEvent' in directiveValue) {
      directiveValue.postSuccessEvent.split(' ').forEach((event) => {
        $(el).trigger(event)
      })
    }
  }
  /**
   * Post check fail actions.
   * 1. hide tool tip if any
   * 2. remove error class if any
   * 3. call post success event if any
   */
  const postFailHandler = (el, directiveValue, options) => {
    $(el).removeAttr('fusioninputchecksuccess')
    $(el).attr('fusioninputcheckbg', '')
    if ('showTip' in directiveValue ? !!directiveValue.showTip : defaultValues.showTip.default) {
      const showTipOnEmpty = options && options.showTipOnEmpty
      if (showTipOnEmpty) {
        $(el).attr('fusioninputchecktip', '')
        if (el.getBoundingClientRect().top !== 0) {
          $(el).trigger(tooltipShowEvent)
        }
      } else if (el.value && el.getBoundingClientRect().top !== 0) {
        $(el).attr('fusioninputchecktip', '')
        $(el).trigger(tooltipShowEvent)
      }
    }
    const errorClass = ('errorClass' in directiveValue && directiveValue.errorClass) || defaultValues.errorClass.default
    if (errorClass) {
      $(el).addClass(errorClass)
    }
    if ('postFailEvent' in directiveValue) {
      directiveValue.postFailEvent.split(' ').forEach((event) => {
        $(el).trigger(event)
      })
    }
  }
  const triggerHandler = (el, binding, vnode) => (event, options) => {
    if (('required' in binding.value) && !binding.value.required) {
      postSuccessHandler(el, binding.value)
    } else if ((event.type === 'blur' && (!el.value))) {
      if ('showTip' in binding.value ? !!binding.value.showTip : defaultValues.showTip.default) {
        $(el).trigger(tooltipHideEvent)
      }
    } else {
      if (checkValueFormat(el, binding.value, (options || {}).allowEmpty)) {
        postSuccessHandler(el, binding.value)
      } else {
        postFailHandler(el, binding.value, options)
      }
    }
  }
  const events = (('triggerEvent' in directiveValue && directiveValue.triggerEvent) || defaultValues.triggerEvent.default)
  events.split(' ').forEach((event) => {
    eventHandlers[event] = triggerHandler
  })
  const resetHandler = (el, binding, node) => (event) => {
    postSuccessHandler(el, binding.value)
  }
  const resetEvents = (('resetEvent' in directiveValue && directiveValue.resetEvent) || defaultValues.resetEvent.default)
  resetEvents.split(' ').forEach((event) => {
    eventHandlers[event] = resetHandler
  })
  return eventHandlers
}

function install (Vue, options) {
  if (install.installed) return
  install.installed = true

  const combinedTooltipOptions = Object.assign({},
    tooltipOptions.defaultValues,
    options || {})
  vtooltip.options = combinedTooltipOptions

  Vue.directive('fusioninputcheck', {
    bind: (el, binding, vnode) => {
      $(el).attr('fusioninputcheck', '')
      const directiveValue = lodash.cloneDeep(binding.value)
      if (!directiveValue) {
        $(el).removeAttr('fusioninputcheck')
        return
      }
      const eventHandlers = createEventHandlers({el, binding, vnode}, directiveValue)
      Object.keys(eventHandlers).forEach((event) => {
        el.addEventListener(event, eventHandlers[event](el, binding, vnode))
        $(el).unbind(event)
        $(el).on(event, eventHandlers[event](el, binding, vnode))
      })

      $(el).unbind(defaultValues.refreshEvent.default)
      $(el).on(defaultValues.refreshEvent.default, () => {
        if ($(el).is('[fusioninputchecktip]') && el.getBoundingClientRect().top !== 0) {
          $(el).trigger(tooltipShowEvent)
        } else if ($(el).is('[fusioninputchecksuccess]') || el.getBoundingClientRect().top === 0) {
          $(el).trigger(tooltipHideEvent)
        }
      })

      binding.value.content = binding.value.tip || defaultValues.tip.default
      if ('showTip' in binding.value ? !!binding.value.showTip : defaultValues.showTip.default) {
        vtooltip.bind(el, binding)
      }
    },
    update: (el, binding, vnode) => {
      $(el).attr('fusioninputcheck', '')
      const directiveValue = lodash.cloneDeep(binding.value)
      if (!directiveValue) {
        $(el).removeAttr('fusioninputcheck')
        return
      }
      const eventHandlers = createEventHandlers({el, binding, vnode}, directiveValue)
      Object.keys(eventHandlers).forEach((event) => {
        el.addEventListener(event, eventHandlers[event](el, binding, vnode))
        $(el).unbind(event)
        $(el).on(event, eventHandlers[event](el, binding, vnode))
      })
      binding.value.content = binding.value.tip || defaultValues.tip.default
      if ('showTip' in binding.value ? !!binding.value.showTip : defaultValues.showTip.default) {
        vtooltip.update(el, binding)
      }
    },
    unbind: (el, { value }) => {
      if (!value) {
        return
      }
      if ('showTip' in value ? !!value.showTip : defaultValues.showTip.default) {
        vtooltip.unbind(el)
      }
    },
  })
}

/**
 * {
 *  type: integer/number/string
 *  pattern:
 *  tip:
 *  triggerEvent: blur validateFusionInputCheck
 *  resetEvent: resetFusionInputCheck
 *  postSuccessEvent:
 *  postFailEvent:
 *  required:
 *  patternOnly: function
 * }
 */
const FusionInputCheck = {
  install,
  get enabled () {
    return tooltipOptions.state.enabled
  },
  set enabled (value) {
    tooltipOptions.state.enabled = value
  },
}

export default FusionInputCheck

export const fusionInputCheckUtil = {
  /**
   * Validate all inputs with fusion-input-check
   * @argument options
   *  allowEmpty:
   *    all for none validation on null values
   *    partial for validation on not nullable values
   *    none for validation on all null values
   *  showTipOnEmpty:
   *    when true, show invalid tooltip on empty required fields (Project Control load only)
   */
  validateAllFields: (options = {allowEmpty: defaultValues.allowEmpty.options.none}, prefixEl) => {
    $(prefixEl || document.documentElement).find('[fusioninputcheck]').trigger('validateFusionInputCheck', options)
  },

  /**
   * Re-validate all fields
   */
  refreshFieldTips: () => {
    $('[fusioninputcheck]').trigger('refreshFusionInputCheckTip')
  },

  /**
   * Clear all existing tips/warnings
   */
  resetAllFields: (prefixEl) => {
    $(prefixEl || document.documentElement).find('[fusioninputcheck]').trigger('resetFusionInputCheck')
  },

  /**
   * Return if current form's fields are valid
   */
  isAllFieldsValid: (prefixEl) => {
    return $(prefixEl || document.documentElement).find('.fusion-input-check-is-invalid-input').length === 0
  },

  /**
   * reset one input filed
   */
  resetInputFiled: inputEl => {
    $(inputEl).trigger('resetFusionInputCheck')
  },
}
